import { useNavigate } from "react-router-dom";
import WhatsAppButton from "@/pages/home/components/WhatsAppButton";
import WazeButton from "@/pages/home/components/WazeButton";
import AccessibilityButton from "@/pages/home/components/AccessibilityButton";
import ScrollToTopButton from "@/pages/home/components/ScrollToTopButton";
import RegistrationModal from "@/pages/home/components/RegistrationModal";
import { useState } from "react";
import image from "../../assets/activity2.jpg";

const whatYouGet = [
  { icon: "ri-focus-3-line", text: "הגדרת מטרות עסקיות ברורות" },
  { icon: "ri-file-list-3-line", text: "בניית תכנית פעולה" },
  { icon: "ri-settings-3-line", text: "כלים לניהול עסק נכון" },
  { icon: "ri-brain-line", text: "חיזוק יכולת קבלת החלטות" },
  { icon: "ri-user-heart-line", text: "ליווי אישי בתהליך" },
  { icon: "ri-line-chart-line", text: "כלים לצמיחה כלכלית" },
];

const whoIsItFor = [
  { icon: "ri-seedling-line", label: "עצמאים בתחילת הדרך" },
  { icon: "ri-store-2-line", label: "בעלי עסקים שרוצים להתקדם" },
  { icon: "ri-pause-circle-line", label: "מי שמרגיש תקוע בעסק" },
  { icon: "ri-plant-line", label: "מי שרוצה לגדול ולהתפתח" },
];

export default function BusinessPage() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div
      className="min-h-screen pb-10"
      style={{ background: "#FAF9F6", direction: "rtl" }}
      dir="rtl"
    >
      {/* Header */}
      <header
        className="sticky top-0 z-40 bg-white border-b-2 border-gray-900 px-4 py-3"
        dir="rtl"
      >
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-gray-900 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
            aria-label="חזרה"
          >
            <i className="ri-arrow-right-line text-xl text-gray-800"></i>
          </button>
          <span className="text-base font-black text-gray-900 tracking-tight">
            ליווי עסקי לעצמאים
          </span>
          <div className="w-10 h-10" />
        </div>
      </header>

      {/* Hero */}
      <section
        className="mx-4 mt-5 rounded-3xl border-[3px] border-gray-900 overflow-hidden"
        style={{ background: "#FEF9C3" }}
      >
        <div className="relative p-6 pb-8">
          <div className="absolute -top-8 -left-8 w-36 h-36 rounded-full bg-yellow-200 opacity-40 pointer-events-none" />
          <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-purple-200 opacity-25 pointer-events-none" />

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-purple-400 border-2 border-gray-900 rounded-full px-3 py-1 mb-4">
            <i className="ri-sparkling-fill text-white text-xs" />
            <span className="text-xs font-black text-white">
              חדש · 800+ עסקים
            </span>
          </div>

          <h1 className="text-3xl font-black text-gray-900 leading-tight mb-2">
            ליווי עסקי לעצמאים
          </h1>
          <p className="text-base font-bold text-yellow-700 mb-3">
            מפתחים את העסק שלך — בצורה חכמה ומדויקת
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            ליווי אישי לפיתוח וקידום העסק, המשלב חשיבה עסקית עם תמיכה לאורך כל
            הדרך. התהליך כולל הגדרת מטרות, קבלת החלטות ובניית תכנית פעולה ברורה.
          </p>
        </div>
      </section>

      {/* Hero image */}
      <div
        className="mx-4 mt-3 rounded-3xl border-[3px] border-gray-900 overflow-hidden"
        style={{ height: 200 }}
      >
        <img
          src={image}
          alt="ליווי עסקי לעצמאים"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* What you get */}
      <section className="mx-4 mt-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-100 border-2 border-yellow-300">
            <i className="ri-gift-line text-yellow-700 text-base" />
          </div>
          <h2 className="text-xl font-black text-gray-900">מה מקבלים?</h2>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {whatYouGet.map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-4 bg-white rounded-2xl border-2 border-gray-900 px-4 py-3.5"
            >
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-900 flex-shrink-0"
                style={{ background: "#FEF9C3" }}
              >
                <i className={`${item.icon} text-yellow-700 text-lg`} />
              </div>
              <span className="text-sm font-bold text-gray-800">
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Who is it for */}
      <section className="mx-4 mt-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-100 border-2 border-purple-300">
            <i className="ri-group-line text-purple-700 text-base" />
          </div>
          <h2 className="text-xl font-black text-gray-900">למי זה מתאים?</h2>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {whoIsItFor.map((item, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-2 bg-white rounded-2xl border-2 border-gray-900 px-3 py-4 text-center"
            >
              <div
                className="w-12 h-12 flex items-center justify-center rounded-2xl border-2 border-gray-900"
                style={{ background: "#EDE9FE" }}
              >
                <i className={`${item.icon} text-purple-700 text-xl`} />
              </div>
              <span className="text-xs font-bold text-gray-800 leading-snug">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Ending line */}
      <section className="mx-4 mt-5 rounded-3xl border-[3px] border-gray-900 overflow-hidden">
        <div
          className="relative p-5 text-center"
          style={{ background: "#FFFBEB" }}
        >
          <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-yellow-200 opacity-40 pointer-events-none" />
          <i className="ri-trophy-line text-3xl text-yellow-600 mb-2 block" />
          <p className="text-sm font-bold text-gray-800 leading-relaxed relative z-10">
            המטרה: לבנות עסק יציב, מתפתח ורווחי — בקצב שמתאים לך
          </p>
        </div>
      </section>

      {/* CTA */}
      <div className="mx-4 mt-5">
        <button
          onClick={() => setModalOpen(true)}
          className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white rounded-2xl py-4 text-base font-black border-2 border-gray-900 hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-base" />
          אני רוצה להתחיל
        </button>
      </div>

      {/* Floating buttons */}
      <WhatsAppButton />
      <AccessibilityButton />
      <ScrollToTopButton />
      <WazeButton />

      <RegistrationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        registerableId={8}
        registerableType="Activity"
      />
    </div>
  );
}
